import { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "@components/buttons/Button";
import ExpertAvailabilityPickerForExperts from "@components/expertSection/schedule/ExpertAvailabilityPickerForExperts";
import styles from "@components/expertSection/schedule/ScheduleSession.module.css";
import SessionRescheduledConfirmation from "@components/expertSection/schedule/SessionRescheduledConfirmation";
import BaseErrors from "@components/fields/BaseErrors";
import PhoneInput from "@components/fields/PhoneInput";
import RadioButtonGroup from "@components/fields/RadioButtonGroup";
import NativeRadioButtonGroup, {
  OptionType as NativeRadioButtonGroupOption
} from "@components/fieldsNative/RadioButtonGroup";
import Form from "@components/forms/Form";
import FormCondition from "@components/forms/FormCondition";
import BodyCopy from "@components/typography/BodyCopy";
import useSessionRescheduler, { FormData } from "@hooks/useSessionRescheduler";
import {
  ConsultDurationEnum,
  ConsultFragment,
  ContactMethodEnum,
  GetJourneyWithExpertAndConsultsQuery
} from "@typing/Generated";
import { contactMethodOptions, durationOptions } from "@utils/optionUtils";

type Props = {
  consult: ConsultFragment;
  journey: GetJourneyWithExpertAndConsultsQuery["journey"];
};

const ExpertRescheduleSession = ({ consult, journey }: Props) => {
  const [originalDuration] = useState(consult.durationType);
  const [originalStartTime] = useState(consult.startTime);

  const { baseErrors, form, onSubmit, state: reschedulerState } = useSessionRescheduler({ consult, journey });
  const intl = useIntl();

  const contactMethod = form.watch("contactMethod");

  const handleDurationChange = useCallback(
    (value: NativeRadioButtonGroupOption | undefined) => {
      value && reschedulerState.setDuration(value.value as ConsultDurationEnum);
    },
    [reschedulerState]
  );

  return (
    <>
      {reschedulerState.rescheduledConsult ? (
        <SessionRescheduledConfirmation
          consult={consult}
          journey={journey}
          originalDuration={originalDuration}
          originalStartTime={originalStartTime}
          rescheduledConsult={reschedulerState.rescheduledConsult}
          setAvailabilitySelected={reschedulerState.setAvailabilitySelected}
          setRescheduledConsult={reschedulerState.setRescheduledConsult}
        />
      ) : (
        <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
          <NativeRadioButtonGroup
            currentSelection={reschedulerState.duration as string}
            fullWidth
            onChange={handleDurationChange}
            options={durationOptions(intl)}
          />
          <div className={styles.label}>
            <BodyCopy color="gray-500">
              <FormattedMessage id="forms.consult.availabilityDateLabel" />
            </BodyCopy>
          </div>
          <ExpertAvailabilityPickerForExperts
            availabilitySelected={reschedulerState.availabilitySelected}
            duration={reschedulerState.duration}
            journey={journey}
            setAvailabilitySelected={reschedulerState.setAvailabilitySelected}
          />
          <div className={styles.label}>
            <BodyCopy color="gray-500">
              <FormattedMessage id="forms.consult.contactMethodLabel" />
            </BodyCopy>
          </div>
          <RadioButtonGroup<FormData>
            control={form.control}
            error={form.formState.errors.contactMethod}
            fullWidth
            name="contactMethod"
            options={contactMethodOptions(intl)}
          />
          <FormCondition condition={contactMethod === ContactMethodEnum.PHONE} fieldName="phoneNumber" form={form}>
            <div className={styles.label}>
              <BodyCopy color="gray-500">
                <FormattedMessage id="forms.consult.expertPhoneNumberLabel" />
              </BodyCopy>
            </div>
            <PhoneInput<FormData>
              autofocus
              control={form.control}
              error={form.formState.errors.phoneNumber}
              name="phoneNumber"
            />
          </FormCondition>
          <div className={styles.expertSubmitBox}>
            <Button
              data-test="submit-button"
              disabled={
                !form?.formState.isValid || form?.formState.isSubmitting || !reschedulerState.availabilitySelected
              }
              type="submit"
            >
              <FormattedMessage id="pages.consults.reschedule.reschedule" />
            </Button>
            <div className="align-center">
              <BaseErrors errors={baseErrors} />
            </div>
          </div>
        </Form>
      )}
    </>
  );
};

export default ExpertRescheduleSession;
